import { z, ZodError } from 'zod'
import { oocApi } from '@/lib/apiClient'
import { oocUrls } from '@/lib/urlBuilder'
import { normalizeZodError } from '@/lib/apiErrors'

export const TenderSchema = z.object({
	ref: z.string(),
	name: z.string(),
	customLabel: z.string().nullable(),
	isTipAllowed: z.boolean(),
	isActive: z.boolean(),
})
export type Tender = z.infer<typeof TenderSchema>

export const OrderTypeSchema = z.object({
	ref: z.string(),
	name: z.string(),
	isAsapOrderAvailable: z.boolean(),
	isAnticipatedOrderAvailable: z.boolean(),
	allowSpecialRequest: z.boolean(),
	// Delivery-specific fields are omitted by the API for non-delivery order types
	deliveryDistanceInMiles: z.number().optional().default(0),
	surchargeFeePercent: z.number().optional().default(0),
	minOrderAmount: z.number().nullable(),
	maxOrderAmount: z.number().nullable(),
	// Ember: order-type.js model attr — used by EtaTime component to display "about X mins" under ASAP.
	// For delivery orders served by DoorDash/DSP, Ember takes Math.max(distanceLeadTimeInMinutes, pickupLeadTimeInMinutes).
	// distanceLeadTimeInMinutes comes from a separate delivery address API call (out of scope until P2-S4-01).
	// For all non-delivery order types (Takeout, Dine-In), pickupLeadTimeInMinutes is used directly.
	pickupLeadTimeInMinutes: z.number().nullable().optional().default(null),
	// API sends null for some order types and omits entirely for others
	storeDeliveryLeadTimeInMinutes: z
		.number()
		.nullish()
		.transform((v) => v ?? 0),
	storeDeliveryValidationType: z.string().optional().default(''),
	isServedByDoorDashDrive: z.boolean().optional().default(false),
	deliveryServedWithDSP: z.boolean().optional().default(false),
})
export type OrderType = z.infer<typeof OrderTypeSchema>

export const MenuScheduleHourSchema = z.object({
	dayOfWeek: z.string(),
	startTime: z.string(),
	endTime: z.string(),
	startDateTime: z.string(),
	endDateTime: z.string(),
	isOvernight: z.boolean(),
})
export type MenuScheduleHour = z.infer<typeof MenuScheduleHourSchema>

export const MenuScheduleSchema = z.object({
	ref: z.string(),
	name: z.string(),
	hours: z.array(MenuScheduleHourSchema),
	duringOffHours: z.boolean(),
})
export type MenuSchedule = z.infer<typeof MenuScheduleSchema>

export const TipSettingsSchema = z.object({
	predefinedTipPercentage1: z.number(),
	predefinedTipPercentage2: z.number(),
	predefinedTipPercentage3: z.number(),
	preselectedTip: z.number(),
	includeTaxInTipSuggestion: z.boolean(),
})
export type TipSettings = z.infer<typeof TipSettingsSchema>

export const DropOffAddressSchema = z.object({
	ref: z.string(),
	name: z.string(),
	addressLine1: z.string(),
	city: z.string(),
	state: z.string(),
	zip: z.string(),
	lastMileLabel: z.string(),
	lastMileInstructions: z.string(),
})
export type DropOffAddress = z.infer<typeof DropOffAddressSchema>

export const LocationDataSchema = z.object({
	id: z.number(),
	locationRef: z.string(),
	name: z.string(),
	isClosed: z.boolean(),
	closureReason: z.string().nullable().optional(),
	oloStoreActive: z.boolean().optional().default(true),
	metaPixelId: z.string().nullable().optional(),
	oloStoreStatus: z.string(),
	systemHealth: z.string(),
	addressLine1: z.string(),
	addressLine2: z.string().nullable(),
	city: z.string(),
	state: z.string(),
	zip: z.string(),
	country: z.string(),
	countryCode: z.string(),
	currency: z.string(),
	distanceUnit: z.string(),
	timeZoneOffset: z.number(),
	logoUrl: z.string().nullable(),
	oloLogoUrl: z.string().nullable(),
	oloContactPhone: z.string().nullable(),
	oloContactEmail: z.string().nullable(),
	allowGiftCardPayments: z.boolean(),
	allowCouponDiscounts: z.boolean(),
	dineInAllowDuringOffHours: z.boolean(),
	reCaptchaEnabled: z.boolean(),
	signInPromptEnabled: z.boolean(),
	deliverToAddresses: z.boolean(),
	isAvsZipCodeRequired: z.boolean(),
	isAvsAddressRequired: z.boolean(),
	addressSelection: z.string(),
	customAddress: z.string().nullable(),
	tenders: z.array(TenderSchema),
	orderTypes: z.array(OrderTypeSchema),
	menuSchedules: z.array(MenuScheduleSchema),
	tipSettings: TipSettingsSchema,
	dropOffAddresses: z.array(DropOffAddressSchema),
})
export type LocationData = z.infer<typeof LocationDataSchema>

const ApiLocationResponseSchema = z.object({
	location: LocationDataSchema,
})

function normalizeLocation(location: LocationData): LocationData {
	const hasActiveCash = location.tenders.some((t) => t.ref === 'CASH' && t.isActive)
	if (!hasActiveCash) {
		return {
			...location,
			tenders: location.tenders.map((t) =>
				t.ref === 'NON_CASH' ? { ...t, isActive: true } : t,
			),
		}
	}
	return location
}

export function getLocation(ref: string): Promise<LocationData> {
	return oocApi
		.get<unknown>(oocUrls.store(ref))
		.then((raw) => {
			const parsed = ApiLocationResponseSchema.parse(raw)
			return normalizeLocation({
				...parsed.location,
				oloStoreActive: parsed.location.oloStoreActive ?? true,
			})
		})
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}

export function getLocationByVanityRef(vanityRef: string): Promise<LocationData> {
	return oocApi
		.get<unknown>(oocUrls.vanityStore(vanityRef))
		.then((raw) => {
			const parsed = ApiLocationResponseSchema.parse(raw)
			return normalizeLocation({
				...parsed.location,
				oloStoreActive: parsed.location.oloStoreActive ?? true,
			})
		})
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}
